import { createSlice } from "@reduxjs/toolkit";
import { api } from "../api";
import {
  getNewDataWithLoadingStates,
  reindexTreeNodes,
  setTemplateData,
  workflowGroupsToRcTree,
} from "../../utils/rctreeDataConversion";
// import { handleLoadingTimeout, isValidJSON } from "../../utils/utilFunctions";
import { camelCase, isArray } from "lodash";

const treeJsonBody = {
  header: {
    rawData: [],
    translatedData: [],
    initialData : [],
    selectedNode: {},
    selectedKeysArray:[]
  },
  body: {
    rawData: [],
    translatedData: [],
    initialData : [],
    selectedNode: {},
    selectedKeysArray:[]
  },
  trailer: {
    rawData: [],
    translatedData: [],
    initialData : [],
    selectedNode: {},
    selectedKeysArray:[]
  },
  condition: [],
  templateHeader: [],
  initialTemplateHeader: [],
  tradingPartner:[],
  initialTradingPartner:[]
}

const treeData = {
  leftTree: {
    tabs: false,
    tree: treeJsonBody,
    currentTab: "body",
    contextAction: false,
    loading: true,
    templateAction:false,
    loadingTemplates:[],
    inboundDependencyTree : { // This will store Inbound Template which is selected in outbound template properties
      header: [],             // This will be used for only frontend.
      body : [],
      trailer: [],
      isLoading: false
    }
  },
  rightTree: {
    tabs: true,
    tree: treeJsonBody,
    currentTab: "body",
    path: "",
    loading: false,
    fetchError: false,
    connectedToDB:false,
    connectedDbTableList:[],
    treeActions:{} // This property can be used to select or expand keys in right tree from any component
  },
  recentlyVisitedTemplates:[],
  pdc : {},
  pdcConnection: {},
  connection: {},
  pdcConnectionList: [],
  pdcNewConnection: {},
  connectedPDC: "",
  pdcListUpdated: false,
  templateChanged: false,
  templateChangedSource: null,
  copyPasteState: {
    copy: false, 
    paste: false ,
    dispatchFrom:'',
    copiedNodes: [],
    copiedErrorHandler:[],
    copiedSourceTemplate:"",
    copiedSourceWorkflow:""
  },
  quickAdd:{recordDelim:'',fieldDelim:""},
  dataValidationDelim:{dataValiadtionRecordDelim:'CRLF', dataValiadtionFieldDelim:'%09'},
  loadingState: {},
  unsavedTemplates: [],
  forceTemplate: {},
  showDocFile: false,
  docFiledata: {blob: null, fileName: ""},
}

export const treeSlice = createSlice({
  name: "treeData",
  initialState: {
    inbound: treeData,
    outbound: treeData,
    workflow: treeData,
    currentScreen: 'inbound', //default to inbound if nothing given on page load
    showHelpPanel: false,
    helpDocList: [],
    unsavedTemplates: {
      inbound_templates: [],
      outbound_templates: [],
      workflow: [],
    },
    dataDepots: false,
    aiAutoMapping: false,
    triggerAiAutoMap: { triggered: false, fromCsvMenu: false },
    trackingReportUpdated: true,
    pendingAcceleratorConfigDialog: null,
    /** When set, AcceleratorWorkflowWizard will auto-advance through workflows until all are configured. */
    acceleratorWorkflowQueue: null, // { workflowGroup, workflows: [{name, title}], currentIndex }
    isInputFocused: false,
    mappingStates: {
      isMappingMode: false,
      isLinkMode: false,
      mappings: [],
      isDragging: false,
      currentMappingTab: 1, // default to body tab
      showOnlySelectedMappings: false, // Default to false - show all mappings initially
      autoMatchingEnabled: true,
      automaticMappings: [],
      dragData: null, // Store drag data for external drag-and-drop
      // Changed from single node to array of nodes to support multiple selections
      selectedSourceNodes: {
        header: [], // Array of selected source nodes in header tab
        body: [],   // Array of selected source nodes in body tab
        trailer: [] // Array of selected source nodes in trailer tab
      },
      selectedTargetNodes: {
        header: [], // Array of selected target nodes in header tab
        body: [],   // Array of selected target nodes in body tab
        trailer: [] // Array of selected target nodes in trailer tab
      },
      expansionRequests: {
        rightTree: {
          header: [],
          body: [],
          trailer: []
        }
      },
      hiddenMappingLines: {
        source: false, // Hide mapping lines from source side (MappingPropertiesContainer)
        target: false  // Hide mapping lines from target side (OutboundTemplatePanel)
      }
    }
},
  reducers: {
    setAiAutoMapping: (state, { payload }) => {
      state.aiAutoMapping = payload;
    },
    setTriggerAiAutoMap: (state, { payload }) => {
      state.triggerAiAutoMap = payload;
    },
    setLeftTreeLoading: (state, { payload }) => {
      state[payload.screen].leftTree.loading = payload.data;
    },
    setRightTreeLoading: (state, { payload }) => {
      state[payload.screen].rightTree.loading = payload.data;
    },
    setLeftTreeCurrentTab: (state, { payload }) => {
      state[payload.screen].leftTree.currentTab = payload.data;
    },
    setRightTreeCurrentTab: (state, { payload }) => {
      state[payload.screen].rightTree.currentTab = payload.data;
    },
    setLeftTreeData: (state, { payload }) => {
      state[payload.screen].leftTree.tree[payload.type].rawData = payload.data;
    },
    setLeftTreeTransData: (state, { payload }) => {
      state[payload.screen].leftTree.tree.body.translatedData = payload.data;
    },
    setLeftTreeSelectedNode: (state, { payload }) => {
      state[payload.screen].leftTree.tree[payload.type].selectedNode = payload.data;
    },
    setRightTreePath: (state, { payload }) => {
      state[payload.screen].rightTree.path = `${payload.workflowGroup}/${payload.template}`;
      // Reset mapping states when template changes
      if (payload.screen === 'outbound') {
        state.mappingStates.mappings = [];
        state.mappingStates.automaticMappings = [];
        state.mappingStates.isDragging = false;
      }
    },
    setRightTreeData: (state, { payload }) => {
      state[payload.screen].rightTree.tree[payload.type].rawData = payload.data || [];
    },
    setRightTreeTransData: (state, { payload }) => {
      state[payload.screen].rightTree.tree[payload.type].translatedData = reindexTreeNodes(payload.data, state[payload.screen].rightTree.tree.templateHeader.FILETYPE) || [];
      state[payload.screen].templateChanged = true;
    },
    setRightTreeSelectedNode: (state, { payload }) => {
      state[payload.screen].rightTree.tree[payload.type].selectedNode = payload?.data || {};
    },
    setRightTreeTemplateHeaders: (state, { payload }) => {
      if (payload.value === "" && state?.[payload?.screen]?.rightTree?.tree?.templateHeader?.hasOwnProperty(payload?.key)) {
        delete state?.[payload?.screen]?.rightTree?.tree?.templateHeader?.[payload?.key]
      }
      else state[payload.screen].rightTree.tree.templateHeader[payload.key] = payload.value;
      state[payload.screen].templateChanged = payload?.templateChanged !== undefined ? payload?.templateChanged : true;
    },
    setRightTreeTemplateHeader: (state, { payload }) => {
      state[payload.screen].rightTree.tree.templateHeader = payload.value;
      state[payload.screen].templateChanged = payload?.templateChanged !== undefined ? payload?.templateChanged : true;
    },
    setWorkflowProperties: (state, { payload }) => {
      state.workflow.rightTree.tree.templateHeader = payload;
      state.workflow.templateChanged = true;
    }, 
    setRightTreeInitialFetchData: (state, { payload }) => {
      state[payload.screen].rightTree.initialFetchData = payload.data;
    },
    setRightTreeInitialTemplateHeader: (state, { payload }) => {
      state[payload.screen].rightTree.tree.initialTemplateHeader = payload.value;
    },
    setLeftTreeContextActionValue: (state, { payload }) => {
      state[payload.screen].leftTree.contextAction = payload.data;
    },
    setShowHelpPanel: (state, { payload }) => {
      state.showHelpPanel = payload;
    },
    setHelpDocList: (state, { payload }) => {
      state.helpDocList = payload;
    },
    setNewNode: (state,{payload})=>{
      if (payload.type === 'body' && payload.screen === 'inbound') {
        state[payload.screen].rightTree.tree[payload.type].translatedData[0].children.push(payload.data);
      } else {
        state[payload.screen].rightTree.tree[payload.type].translatedData.push(payload.data);
      }
      state[payload.screen].templateChanged = true;
    },
    setCurrentScreen:(state,{payload})=>{
      state.currentScreen = payload;
    },
    setNewRightTreeTranslatedData:(state,{payload})=>{
      const {type,data, templateChanged} = payload;
      state[payload.screen].rightTree.tree[type].translatedData = reindexTreeNodes(data, state?.[payload?.screen]?.rightTree?.tree?.templateHeader?.FILETYPE || "") || [];
      state[payload.screen].templateChanged = templateChanged !== undefined ? templateChanged : true;
    },
    setSelectedKeysArray:(state,{payload})=>{
      const {type,data} = payload;
      state[payload.screen].rightTree.tree[type].selectedKeysArray = data;
    },
    setRetrievedData:(state,{payload}) =>{
      setTemplateData(state,payload,true);
    },
    setTemplateAction:(state,{payload}) =>{
      const {screen,value} = payload;
      state[screen].leftTree.templateAction = value; 
    },
    setTradingPartner:(state,{payload}) =>{
      const {screen,value} = payload;
      state[screen].rightTree.tree.tradingPartner = value;
    },
    setConnectedToDB:(state,{payload}) =>{
      const {screen,value} = payload;
      state[screen].rightTree.connectedToDB = value;
    },
    setConnectedDbTableList:(state,{payload}) =>{
      const {screen,value} = payload;
      state[screen].rightTree.connectedDbTableList = value;
    },
    setPdcInfo: (state,{payload}) => {
      const {screen, value} = payload
      state[screen].pdc = value;
    },
    setCurrentPdcConnectionDetail: (state, {payload}) => {
      const {screen, value} = payload
      state[screen].pdcConnection = value;
    },
    setPdcConnectionsList: (state, { payload}) => {
      const {screen, value} = payload
      state[screen].pdcConnectionList = value;
    },
    setConnection: (state, { payload}) => {
      const {screen, value} = payload
      state[screen].connection = value;
    },
    setNewPdcConnection: (state, { payload }) => {
      const {screen, value} = payload
      state[screen].pdcNewConnection= value;
    },
    setForceTemplate: (state, {payload}) =>{
      state[payload.screen].forceTemplate = payload;
    },
    setUnsavedTemplates: (state, {payload}) => {
      state.unsavedTemplates = payload;
    },
    setShowDocFile: (state, {payload}) => {
      state[state.currentScreen].showDocFile = payload
    },
    setQuickAdd:(state,{payload}) =>{
      state[state.currentScreen].quickAdd = payload.data
    },
    setTreeActions:(state,{payload}) =>{
      state[state.currentScreen].rightTree.treeActions = payload?.data;
    },
    setLoadingActions: (state, {payload}) => {
      state[state.currentScreen].loadingState[payload.key] = {...state?.[state?.currentScreen]?.loadingState?.[payload?.key] || {}, ...payload.data};
    },
    setTemplateChanged: (state, {payload}) => {
      state[payload.screen].templateChanged = payload.data;
    },
    setConnectedPDC : (state, {payload}) => {
      state[state.currentScreen].connectedPDC = payload.data;
    },
    setPdcListUpdated: (state, { payload }) => {
      state.pdcListUpdated = payload.status;
    },
    setCopyPasteState: (state, {payload}) => {
      state[state.currentScreen].copyPasteState = payload.data;
    },
    setInboundDependencyTree : (state, {payload}) => {
      state[state.currentScreen].leftTree.inboundDependencyTree = payload.data;
    },
    setDataDepotObj: (state, {payload}) => {
      state.dataDepots = payload;
    },
    setRecentlyVisitedTemplates: (state, { payload }) => {
      state[payload.screen].recentlyVisitedTemplates = payload.value
    },
    setDataValidationDelim:(state,{payload}) =>{
      state[state.currentScreen].dataValidationDelim = payload.data
    },
    setLoadingLeftData:(state, {payload}) => {
      const leftTreeData = state[payload.screen].leftTree.tree.body.translatedData;
      const newLeftTreeData = getNewDataWithLoadingStates(leftTreeData, payload.templateName, payload.workflowGroupName, payload.isLoading);
      state[payload.screen].leftTree.tree.body.translatedData = newLeftTreeData
    },
    setTemplateFetchError: (state, {payload}) => {
      state[payload.screen].rightTree.fetchError = payload.data;
    },
    setTrackingReportUpdated: (state, ) => {
      state.trackingReportUpdated = !state?.trackingReportUpdated;
    },
    setPendingAcceleratorConfigDialog: (state, { payload }) => {
      state.pendingAcceleratorConfigDialog = payload ?? null;
    },
    clearPendingAcceleratorConfigDialog: (state) => {
      state.pendingAcceleratorConfigDialog = null;
    },
    setAcceleratorWorkflowQueue: (state, { payload }) => {
      state.acceleratorWorkflowQueue = payload ?? null;
    },
    setAcceleratorWorkflowCurrentIndex: (state, { payload }) => {
      if (state.acceleratorWorkflowQueue) state.acceleratorWorkflowQueue.currentIndex = payload;
    },
    clearAcceleratorWorkflowQueue: (state) => {
      state.acceleratorWorkflowQueue = null;
    },
    setDocFileData: (state, {payload}) => {
      state[payload.screen].docFiledata = {
        blob: payload.blob,
        fileName: payload.fileName
     }
    },
    setIsMappingMode: (state, {payload}) => {
      state.mappingStates.isMappingMode = payload;
      // When mapping mode is turned off, also turn off link mode
      if (!payload) {
        state.mappingStates.isLinkMode = false;
      }
    },
    setIsLinkMode: (state, {payload}) => {
      state.mappingStates.isLinkMode = payload;
    },
    addMapping: (state, {payload}) => {
      state.mappingStates.mappings.push(payload);
    },
    removeMapping: (state, {payload}) => {
      state.mappingStates.mappings = state.mappingStates.mappings.filter((_, index) => index !== payload);
    },
    clearMappings: (state) => {
      state.mappingStates.mappings = [];
    },
    setMappings: (state, {payload}) => {
      state.mappingStates.mappings = payload;
    },
    setIsDragging: (state, {payload}) => {
      state.mappingStates.isDragging = payload;
    },
    setCurrentMappingTab: (state, {payload}) => {
      state.mappingStates.currentMappingTab = payload;
    },
    setShowOnlySelectedMappings: (state, {payload}) => {
      state.mappingStates.showOnlySelectedMappings = payload;
    },
    setAutoMatchingEnabled: (state, {payload}) => {
      state.mappingStates.autoMatchingEnabled = payload;
    },
    addAutomaticMapping: (state, {payload}) => {
      const exists = state.mappingStates.automaticMappings.some(m => 
        m.sourceKey === payload.sourceKey && m.targetKey === payload.targetKey
      );
      if (!exists) {
        state.mappingStates.automaticMappings.push(payload);
      }
    },
    removeAutomaticMapping: (state, {payload}) => {
      state.mappingStates.automaticMappings = state.mappingStates.automaticMappings.filter(
        (_, index) => index !== payload
      );
    },
    clearAutomaticMappings: (state) => {
      state.mappingStates.automaticMappings = [];
    },
    setAutomaticMappings: (state, {payload}) => {
      state.mappingStates.automaticMappings = payload;
    },
    setDragData: (state, {payload}) => {
      state.mappingStates.dragData = payload;
    },
    clearDragData: (state) => {
      state.mappingStates.dragData = null;
    },
    removeMappingsByTargetKey: (state, {payload}) => {
      // Remove any existing mappings to the same target key (enforce one target, one source rule)
      state.mappingStates.mappings = state.mappingStates.mappings.filter(
        mapping => mapping.targetKey !== payload.targetKey
      );
      state.mappingStates.automaticMappings = state.mappingStates.automaticMappings.filter(
        mapping => mapping.targetKey !== payload.targetKey
      );
    },
    resetMappingStates: (state) => {
      state.mappingStates.mappings = [];
      state.mappingStates.automaticMappings = [];
      state.mappingStates.isDragging = false;
      state.mappingStates.showOnlySelectedMappings = false; // Default to show all mappings
      state.mappingStates.selectedSourceNodes = {
        header: [],
        body: [],
        trailer: []
      };
      state.mappingStates.selectedTargetNodes = {
        header: [],
        body: [],
        trailer: []
      };
      state.mappingStates.expansionRequests = {
        rightTree: {
          header: [],
          body: [],
          trailer: []
        }
      };
      state.mappingStates.hiddenMappingLines = {
        source: false,
        target: false
      };
    },
    setMappingSelectedSourceNode: (state, {payload}) => {
      const tabMapping = ['header', 'body', 'trailer'];
      const tabName = tabMapping[payload.tab] || payload.tab;
      if (payload.selectedNode) {
        state.mappingStates.selectedSourceNodes[tabName] = [payload.selectedNode];
      } else {
        state.mappingStates.selectedSourceNodes[tabName] = [];
      }
    },
    setMappingSelectedSourceNodes: (state, {payload}) => {
      const tabMapping = ['header', 'body', 'trailer'];
      const tabName = tabMapping[payload.tab] || payload.tab;
      state.mappingStates.selectedSourceNodes[tabName] = payload.selectedNodes || [];
    },
    setMappingSelectedTargetNodes: (state, {payload}) => {
      const tabMapping = ['header', 'body', 'trailer'];
      const tabName = tabMapping[payload.tab] || payload.tab;
      state.mappingStates.selectedTargetNodes[tabName] = payload.selectedNodes || [];
    },
    requestTreeExpansion: (state, {payload}) => {
      // payload: { tree: 'rightTree', tab: 'body', keysToExpand: ['key1', 'key2'] }
      const { tree, tab, keysToExpand } = payload;
      if (tree === 'rightTree' && keysToExpand && keysToExpand.length > 0) {
        // Add timestamp to ensure this always triggers a re-render even with same keys
        state.mappingStates.expansionRequests.rightTree[tab] = {
          keys: keysToExpand,
          timestamp: Date.now()
        };
      }
    },
    clearExpansionRequest: (state, {payload}) => {
      // payload: { tree: 'rightTree', tab: 'body' }
      const { tree, tab } = payload;
      if (tree === 'rightTree') {
        state.mappingStates.expansionRequests.rightTree[tab] = null;
      }
    },
    setHiddenMappingLines: (state, {payload}) => {
      // payload: { side: 'source' | 'target', hidden: boolean }
      state.mappingStates.hiddenMappingLines[payload.side] = payload.hidden;
    },
    hideSourceMappingLines: (state) => {
      state.mappingStates.hiddenMappingLines.source = true;
    },
    showSourceMappingLines: (state) => {
      state.mappingStates.hiddenMappingLines.source = false;
    },
    hideTargetMappingLines: (state) => {
      state.mappingStates.hiddenMappingLines.target = true;
    },
    showTargetMappingLines: (state) => {
      state.mappingStates.hiddenMappingLines.target = false;
    },
    showAllMappingLines: (state) => {
      state.mappingStates.hiddenMappingLines.source = false;
      state.mappingStates.hiddenMappingLines.target = false;
    },
    setIsInputFocusedTrue: (state) => {
      state.isInputFocused = true;
    },
    setIsInputFocusedFalse: (state) => {
      state.isInputFocused = false;
    },
    setTemplateChangedSource: (state, {payload}) => {
      state[payload.screen].templateChangedSource = payload.data;
    }
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(api.endpoints.getHelpTreeList.matchFulfilled,
        (state, {payload}) => {
          if(payload){
            state.helpDocList = payload?.documentation_list;
          }
        })
      .addMatcher(api.endpoints.getSearchHelpDocs.matchFulfilled, 
        (state, {payload}) => {
        if(payload){
          state.helpDocList = payload?.documentation_list;
        }
      })
      .addMatcher( //inbound page, left tree
        api.endpoints.getInboundTemplateList.matchFulfilled,
        (state, { payload }) => {
          if (payload?.current_state === "SUCCESS") { 
            state['inbound'].leftTree.tree.body.rawData = payload.data;
            state['inbound'].leftTree.tree.body.translatedData = workflowGroupsToRcTree(payload.data, 'inbound');
          }
          state['inbound'].leftTree.loading = false;
        }
      )
      .addMatcher( //inbound page, right tree
        api.endpoints.getInboundTemplate.matchFulfilled,
        (state, { payload, meta: { arg : { originalArgs : { workflowGroupName, templateName, template_name} } } }) => {
          let tn = templateName || template_name || false;
          if (payload.current_state === "ERROR") {
            state["inbound"].rightTree.loading = false
            state["inbound"].rightTree.fetchError = true;
            return;
          }
          const isValidTemplate = !!workflowGroupName && !!tn // If selected node have workflow and template name then only it is valid.
          state.inbound.templateChanged = false;
          state['inbound'].rightTree.initialFetchData = payload;
          setTemplateData(state, {...payload, screen: 'inbound'} , isValidTemplate);
        }
      )
      .addMatcher( //outbound page, left tree
        api.endpoints.getOutboundTemplateList.matchFulfilled,
        (state, { payload }) => {
          if (payload?.current_state === "SUCCESS") {
            state['outbound'].leftTree.tree.body.rawData = payload.data;
            state['outbound'].leftTree.tree.body.translatedData = workflowGroupsToRcTree(payload.data, 'outbound');
          }
          state['outbound'].leftTree.loading = false;
        }
      )
      .addMatcher( //outbound page, right tree
        api.endpoints.getOutboundTemplate.matchFulfilled,
        (state, { payload, meta: { arg : { originalArgs : { workflowGroupName, templateName, template_name} } } }) => {
          let tn = templateName || template_name || false;
          if (
            // this was causing problem because get_oubound_template sends the template data, with no "payload.current_state"
            // payload.current_state === "ERROR"
            !payload || (payload && payload?.current_state === "ERROR")
          ) {
            state["outbound"].rightTree.loading = false
            state["outbound"].rightTree.fetchError = true;
            return;
          }
          const isValidTemplate = !!workflowGroupName && !!tn // If selected node have workflow and template name then only it is valid.
          state['outbound'].rightTree.initialFetchData = payload;
          setTemplateData(state, {...payload, screen: 'outbound'}, isValidTemplate);
        }
      )
      .addMatcher( //workflow, left tree
        api.endpoints.getWorkflows.matchFulfilled,
        (state, { payload }) => {
          if (payload?.current_state === "SUCCESS") {
            state['workflow'].leftTree.tree.body.rawData = payload.data;
            state['workflow'].leftTree.tree.body.translatedData = workflowGroupsToRcTree(payload.data, 'workflow');
          }
          state['workflow'].leftTree.loading = false;
        }
      )
      .addMatcher( //workflow, right tree
        api.endpoints.getWorkflowTree.matchFulfilled,
        (state, { payload, meta: { arg : { originalArgs : {account, workflowGroupName, templateName} } } }) => {
         // Create a deep copy of the payload and assign it to initialFetchData.
          const wfData =  payload ? {
            ...payload?.WORKFLOW_DEFINITION,
            WORKFLOW: {
              ...payload?.WORKFLOW_DEFINITION?.WORKFLOW,
              WF_ITEM: [...(payload?.WORKFLOW_DEFINITION?.WORKFLOW.WF_ITEM || [])]
            }
          } : {};
          let tn = templateName || false;
          if (
            // this was causing problem because get_oubound_template sends the template data, with no "payload.current_state"
            // payload.current_state === "ERROR"
            !payload || (payload && payload?.current_state === "ERROR")
          ) {
            state["workflow"].rightTree.loading = false
            state["workflow"].rightTree.fetchError = true;
            return;
          }
          const isValidTemplate = !!workflowGroupName && !!tn // If selected node have workflow and template name then only it is valid.
          state['workflow'].rightTree.initialFetchData = {WORKFLOW_DEFINITION: wfData};
          setTemplateData(state, {...payload, screen: 'workflow'}, isValidTemplate);
        }
      )
      .addMatcher(
        api.endpoints.getUnsavedTemplates.matchFulfilled,
        (state, { payload }) => {
          if (payload?.data) state.unsavedTemplates = payload.data
        }
      )
      .addMatcher(
        api.endpoints.getUnsavedInboundTemplates.matchFulfilled,
        (state, { payload }) => {
          //set unsavedTemplates array, or empty to avoid undefined on failure.
          state.inbound.unsavedTemplates = payload.unsaved_inbound_templates || [];
        }
      )
      .addMatcher(api.endpoints.getPdcList.matchFulfilled,(state, args) =>{
        const {payload, meta: { arg : { originalArgs : { direction } } }} = args;
        if(payload && direction) {
          const pdcConnections = {};
          payload?.predefined_connections?.forEach( e => pdcConnections[e] = {});
          state[direction === "in" ? "inbound" : "outbound"].pdc = pdcConnections;
        }
      })
      .addMatcher(api.endpoints.getSchemaList.matchFulfilled,(state, args) =>{
        const {payload, meta: { arg : { originalArgs : { direction, pdc_name } } }} = args;
        state[direction === "in" ? "inbound" : "outbound"].loadingState['SCHEMA_LOADING'] = {isLoading :false , isError: false};
        if(payload && direction && pdc_name  && payload?.current_state === 'SUCCESS') {
          const schema = {};
          if(isArray(payload?.data)) payload?.data?.forEach( e => schema[e] = {});
          state[direction === "in" ? "inbound" : "outbound"].pdc[pdc_name] = schema;
        }
      })
      .addMatcher(api.endpoints.getSchemaList.matchRejected, (state, args) => {
        const { meta: { arg : { originalArgs : { direction } } }} = args;
        state[direction === "in" ? "inbound" : "outbound"].loadingState['SCHEMA_LOADING'] = {isLoading :false , isError: true};
      })
      .addMatcher(api.endpoints.connect_to_selected_db.matchFulfilled, (state, args) => {
        // const {meta: { arg : { originalArgs : {} } }} = args; 
        // state[direction === "IN" ? "inbound" : "outbound"].loadingState['SCHEMA_LOADING'] = {isLoading :false , isError: false};
      })
      .addMatcher(api.endpoints.connect_to_selected_db.matchRejected, (state, args) => {
        const { meta: { arg : { originalArgs : { direction } } }} = args;
        state[direction === "IN" ? "inbound" : "outbound"].loadingState['SCHEMA_LOADING'] = {isLoading :false , isError: true};
      })
      .addMatcher(api.endpoints.get_database_table_info.matchFulfilled,(state, args) =>{
        const {payload, meta: { arg : { originalArgs : { direction, pdc_name, schema_name } } }} = args;
        state[direction === "IN" ? "inbound" : "outbound"].loadingState['TABLE_LOADING'] = {isLoading :false , isError: false};
        if(payload && direction && pdc_name && schema_name  && payload?.current_state === 'SUCCESS') {
          const tables = {};
          payload?.data?.database_table_list?.forEach( e => tables[e] = {});
          state[direction === "IN" ? "inbound" : "outbound"].pdc[pdc_name][schema_name] = tables;
        }
      })
      .addMatcher(api.endpoints.get_database_table_info.matchRejected,(state, args) =>{
        const { meta: { arg : { originalArgs : { direction } } }} = args;
        state[direction === "IN" ? "inbound" : "outbound"].loadingState['TABLE_LOADING'] = {isLoading :false , isError: true};
      })
      .addMatcher(api.endpoints.get_database_column_info.matchFulfilled,(state, args) =>{
        const {payload, meta: { arg : { originalArgs : { direction, pdc_name,table_name,schema_name } } }} = args;
        let tabDir = direction === "IN" ? "inbound" : "outbound";
        state[tabDir].loadingState['COLUMN_LOADING'] = {isLoading :false , isError: true};
        if(payload  && direction && pdc_name && table_name && payload?.current_state === 'SUCCESS') {
          const columns = {};
          payload?.data?.database_column_list?.forEach( e => columns[e] = {});

          if (state[tabDir].pdc[pdc_name] && state[tabDir].pdc[pdc_name][schema_name] !== undefined)
            state[tabDir].pdc[pdc_name][schema_name][table_name] = columns;
        }
      })
      .addMatcher(api.endpoints.get_database_column_info.matchRejected,(state, args) =>{
        const {meta: { arg : { originalArgs : { direction } } }} = args;
        state[direction === "IN" ? "inbound" : "outbound"].loadingState['COLUMN_LOADING'] = {isLoading :false , isError: true};
      })
      .addMatcher(api.endpoints.get_metadata.matchFulfilled,(state, args) => {
        const {meta: { arg : { originalArgs : { direction } } }} = args;
        state[direction === "IN" ? "inbound" : "outbound"].loadingState['META_LOADING'] = {isLoading :false , isError: false};
      })
      .addMatcher(api.endpoints.get_metadata.matchRejected,(state, args) => {
        const {meta: { arg : { originalArgs : { direction } } }} = args;
        state[direction === "IN" ? "inbound" : "outbound"].loadingState['META_LOADING'] = {isLoading :false , isError: true};
      })
      .addMatcher(api.endpoints.get_top_25_records.matchFulfilled,(state, args) => {
        const {meta: { arg : { originalArgs : { direction } } }} = args;
        state[direction === "IN" ? "inbound" : "outbound"].loadingState['TOP25_LOADING'] = {isLoading :false , isError: false};
      })
      .addMatcher(api.endpoints.get_top_25_records.matchRejected,(state, args) => {
        const { meta: { arg : { originalArgs : { direction } } }} = args;
        state[direction === "IN" ? "inbound" : "outbound"].loadingState['TOP25_LOADING'] = {isLoading :false , isError: true};
      })
      .addMatcher(api.endpoints.compare_sql.matchFulfilled,(state, args) => {
        const { meta: { arg : { originalArgs : { direction } } }} = args;
        state[direction === "IN" ? "inbound" : "outbound"].loadingState['COMPARE_LOADING'] = {isLoading :false , isError: false};
      })
      .addMatcher(api.endpoints.compare_sql.matchRejected,(state, args) => {
        const { meta: { arg : { originalArgs : { direction } } }} = args;
        state[direction === "IN" ? "inbound" : "outbound"].loadingState['COMPARE_LOADING'] = {isLoading :false , isError: true};
      })
      .addMatcher(api.endpoints.get_sql_result.matchFulfilled,(state, args) => {
        const { meta: { arg : { originalArgs : { direction } } }} = args;
        state[direction === "IN" ? "inbound" : "outbound"].loadingState['SQL_LOADING'] = {isLoading :false , isError: false};
      })
      .addMatcher(api.endpoints.get_sql_result.matchRejected,(state, args) => {
        const { meta: { arg : { originalArgs : { direction } } }} = args;
        state[direction === "IN" ? "inbound" : "outbound"].loadingState['SQL_LOADING'] = {isLoading :false , isError: true};
      })
      .addMatcher(api.endpoints.saveUserInboundTemplate.matchPending, (state, args) => {
        const { meta: { arg : { originalArgs : {  templateName, workflowGroupName } } }} = args;
        const leftTreeData = state.inbound.leftTree.tree.body.translatedData;
        const newLeftTreeData = getNewDataWithLoadingStates(leftTreeData, templateName, workflowGroupName, true);
        state.inbound.leftTree.tree.body.translatedData = newLeftTreeData
        const decodedWorkflowName = decodeURIComponent(workflowGroupName);
        const decodedTemplateName = decodeURIComponent(templateName);
        if (state.inbound.leftTree.tree.body.selectedNode?.title === decodedTemplateName && state.inbound.leftTree.tree.body.selectedNode?.workflowGroup === decodedWorkflowName) {
          state.inbound.leftTree.tree.body.selectedNode.disabled = {
            ...state.inbound.leftTree.tree.body.selectedNode,
            disabled: true
          };
        }
      })
      .addMatcher(api.endpoints.saveUserInboundTemplate.matchFulfilled, (state, args) => {
        const { meta: { arg : { originalArgs : { templateName, workflowGroupName } } }} = args;
        const leftTreeData = state.inbound.leftTree.tree.body.translatedData;
        const newLeftTreeData = getNewDataWithLoadingStates(leftTreeData, templateName, workflowGroupName, false);
        state.inbound.leftTree.tree.body.translatedData = newLeftTreeData
        const decodedWorkflowName = decodeURIComponent(workflowGroupName);
        const decodedTemplateName = decodeURIComponent(templateName);
        if (state.inbound.leftTree.tree.body.selectedNode?.title === decodedTemplateName && state.inbound.leftTree.tree.body.selectedNode?.workflowGroup === decodedWorkflowName) {
          state.inbound.leftTree.tree.body.selectedNode.disabled = {
            ...state.inbound.leftTree.tree.body.selectedNode,
            disabled: false
          };
        }
      })
      .addMatcher(api.endpoints.saveUserOutboundTemplate.matchPending, (state, args) => {
        const { meta: { arg : { originalArgs : {  templateName, workflowGroupName, source } } }} = args;
        const leftTreeData = state.outbound.leftTree.tree.body.translatedData;
        const newLeftTreeData = getNewDataWithLoadingStates(leftTreeData, templateName, workflowGroupName, true);
        state.outbound.leftTree.tree.body.translatedData = newLeftTreeData
        const decodedWorkflowName = decodeURIComponent(workflowGroupName);
        const decodedTemplateName = decodeURIComponent(templateName);
        if (state.outbound.leftTree.tree.body.selectedNode?.title === decodedTemplateName && state.outbound.leftTree.tree.body.selectedNode?.workflowGroup === decodedWorkflowName) {
          state.outbound.leftTree.tree.body.selectedNode.disabled = {
            ...state.outbound.leftTree.tree.body.selectedNode,
            disabled: true
          };
        }
      })
      .addMatcher(api.endpoints.saveUserOutboundTemplate.matchFulfilled, (state, args) => {
        const { meta: { arg : { originalArgs : { templateName, workflowGroupName, source } } }} = args;
        const leftTreeData = state.outbound.leftTree.tree.body.translatedData;
        const newLeftTreeData = getNewDataWithLoadingStates(leftTreeData, templateName, workflowGroupName, false);
        state.outbound.leftTree.tree.body.translatedData = newLeftTreeData
        const decodedWorkflowName = decodeURIComponent(workflowGroupName);
        const decodedTemplateName = decodeURIComponent(templateName);
        if (state.outbound.leftTree.tree.body.selectedNode?.title === decodedTemplateName && state.outbound.leftTree.tree.body.selectedNode?.workflowGroup === decodedWorkflowName) {
          state.outbound.leftTree.tree.body.selectedNode.disabled = {
            ...state.outbound.leftTree.tree.body.selectedNode,
            disabled: false
          };
        }
        state.outbound.templateChangedSource = source;
      })
      .addMatcher(api.endpoints.saveUserWorkflowTemplate.matchPending, (state, args) => {
        const { meta: { arg : { originalArgs : {  templateName, workflowGroupName } } }} = args;
        const leftTreeData = state.workflow.leftTree.tree.body.translatedData;
        const newLeftTreeData = getNewDataWithLoadingStates(leftTreeData, templateName, workflowGroupName, true);
        state.workflow.leftTree.tree.body.translatedData = newLeftTreeData
        const decodedWorkflowName = decodeURIComponent(workflowGroupName);
        const decodedTemplateName = decodeURIComponent(templateName);
        if (state.workflow.leftTree.tree.body.selectedNode?.title === decodedTemplateName && state.workflow.leftTree.tree.body.selectedNode?.workflowGroup === decodedWorkflowName) {
          state.workflow.leftTree.tree.body.selectedNode.disabled = {
            ...state.workflow.leftTree.tree.body.selectedNode,
            disabled: true
          };
        }
        
      })
      .addMatcher(api.endpoints.saveUserWorkflowTemplate.matchFulfilled, (state, args) => {
        const { meta: { arg : { originalArgs : { templateName, workflowGroupName, source } } }} = args;
        const leftTreeData = state.workflow.leftTree.tree.body.translatedData;
        const newLeftTreeData = getNewDataWithLoadingStates(leftTreeData, templateName, workflowGroupName, false);
        const decodedWorkflowName = decodeURIComponent(workflowGroupName);
        const decodedTemplateName = decodeURIComponent(templateName);
        state.workflow.leftTree.tree.body.translatedData = newLeftTreeData
        if (state.workflow.leftTree.tree.body.selectedNode?.title === decodedTemplateName && state.workflow.leftTree.tree.body.selectedNode?.workflowGroup === decodedWorkflowName) {
          state.workflow.leftTree.tree.body.selectedNode = {
            ...state.workflow.leftTree.tree.body.selectedNode,
            disabled: false
          };
        }
        state.workflow.templateChangedSource = source;
      })
      .addMatcher( //reload outbound tree with new data after ai mapping.
        api.endpoints.getAiAutoMapping.matchFulfilled,
        (state, { payload, meta: { arg : { originalArgs : { user, account, wfgIn, inbound, wfgOut, outbound } } } }) => {
         
          state['outbound'].rightTree.initialFetchData = payload;
          setTemplateData(state, {...payload, screen: 'outbound'}, true);
        }
      )
      .addDefaultCase((state, action) => {
        if (action?.error?.message === "Rejected") {
          //LEAVING THIS CONSOLE LOG IN TO DEBUG PORTAL BEHAVIOR. REMOVE AFTER DEV.
          // console.log('DEV: endpoint action rejected: ', action);
          let preventRedirect = false;
          let redirectUrl = '';
          const urlParams = new URLSearchParams(window.location.search);
          for (const [key, value] of urlParams) {
            if (camelCase(key) === "baseUrl") redirectUrl = `${value}/auth_bridgegate/login`;
            if (key === "preventRedirect" && value === "true") preventRedirect = true;
          }
          if (action?.payload?.status === "PARSING_ERROR") {
            //case: invalid json from some endpoint response. will need to handle this is future.
            preventRedirect = true;
          }
          if (action?.payload?.status === "FETCH_ERROR") {
            //case: internet issue. will need handler for this.
            preventRedirect = true;
          }
          //add url flag "preventRedirect" for use in dev environment to prevent redirect when token expires/server is down.
          if (!preventRedirect) {
            if (action.meta?.baseQueryMeta?.request?.url?.length > 0) {
              // if user session expired, redirect to portal login page (use baseUrl from the request itself).
              let baseUrl = action.meta.baseQueryMeta.request.url.split("/workbench?")[0];
              window.top.location.href = `${baseUrl}/auth_bridgegate/login`;
            } else {
              //backup method, use baseUrl from url search.
              if (!preventRedirect) window.top.location.href = redirectUrl;
            }
          }
        }
      })
  },
});

export const treeActions = {
  ...treeSlice.actions,
};

export default treeSlice.reducer;
